import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface AnswerRow {
  id: string;
  title: string;
  keywords: string[];
  answer: string;
  lang: string;
  active: boolean;
  updated_at: string;
}

const emptyDraft = { title: "", keywords: "", answer: "", lang: "de", active: true };

const AdminMikrobiAnswers = () => {
  const [rows, setRows] = useState<AnswerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [params] = useSearchParams();
  const { toast } = useToast();

  useEffect(() => {
    const topic = params.get("topic");
    if (topic) setDraft((d) => ({ ...d, title: topic }));
  }, [params]);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("mikrobi_answers")
      .select("id, title, keywords, answer, lang, active, updated_at")
      .order("updated_at", { ascending: false })
      .returns<AnswerRow[]>();
    if (error) toast({ title: "Fehler beim Laden", description: error.message, variant: "destructive" });
    setRows(data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    if (!draft.title.trim() || !draft.answer.trim()) {
      toast({ title: "Titel und Antwort werden benötigt", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase.from("mikrobi_answers").insert({
      title: draft.title.trim(),
      keywords: draft.keywords
        .split(",")
        .map((k) => k.trim().toLowerCase())
        .filter(Boolean),
      answer: draft.answer.trim(),
      lang: draft.lang,
      active: draft.active,
    });
    setSaving(false);
    if (error) {
      toast({ title: "Fehler beim Speichern", description: error.message, variant: "destructive" });
      return;
    }
    setDraft(emptyDraft);
    toast({ title: "Antwort gespeichert", description: "Mikrobi nutzt sie ab sofort." });
    load();
  };

  const update = async (row: AnswerRow, patch: Partial<AnswerRow>) => {
    const { error } = await supabase.from("mikrobi_answers").update(patch).eq("id", row.id);
    if (error) {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
      return;
    }
    setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, ...patch } : r)));
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("mikrobi_answers").delete().eq("id", id);
    if (error) {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
      return;
    }
    setRows((rs) => rs.filter((r) => r.id !== id));
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto max-w-4xl space-y-8 px-4 py-10">
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
            <Link to="/admin/mikrobi">
              <ArrowLeft className="mr-2 h-4 w-4" /> Auswertung
            </Link>
          </Button>
          <h1 className="text-3xl font-semibold">Mikrobis Antworten pflegen</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Hinterlegen Sie zu häufigen Themen eigene Antworten. Mikrobi bekommt sie bei jeder
            Anfrage als bevorzugtes Wissen mit und formuliert daraus – im gewohnten Ton, ohne dass
            etwas programmiert werden muss. Änderungen wirken innerhalb weniger Minuten.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Neue Antwort</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="title">Titel / Thema</Label>
                <Input
                  id="title"
                  value={draft.title}
                  maxLength={120}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="z. B. Reizdarm / SIBO"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="keywords">Stichwörter (Komma getrennt)</Label>
                <Input
                  id="keywords"
                  value={draft.keywords}
                  maxLength={300}
                  onChange={(e) => setDraft({ ...draft, keywords: e.target.value })}
                  placeholder="reizdarm, sibo, blähbauch"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="answer">Antwort</Label>
              <Textarea
                id="answer"
                rows={6}
                maxLength={4000}
                value={draft.answer}
                onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
                placeholder="So soll Mikrobi zu diesem Thema antworten …"
              />
            </div>
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Label htmlFor="lang" className="text-sm">
                  Sprache
                </Label>
                <select
                  id="lang"
                  value={draft.lang}
                  onChange={(e) => setDraft({ ...draft, lang: e.target.value })}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="de">Deutsch</option>
                  <option value="en">Englisch</option>
                  <option value="both">Beide</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="active"
                  checked={draft.active}
                  onCheckedChange={(v) => setDraft({ ...draft, active: v })}
                />
                <Label htmlFor="active" className="text-sm">
                  Aktiv
                </Label>
              </div>
              <Button onClick={create} disabled={saving} className="ml-auto">
                <Plus className="mr-2 h-4 w-4" /> Speichern
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Gepflegte Antworten</h2>
          {loading && <p className="text-sm text-muted-foreground">Wird geladen …</p>}
          {!loading && rows.length === 0 && (
            <p className="text-sm text-muted-foreground">Noch keine Antworten hinterlegt.</p>
          )}
          {rows.map((row) => (
            <Card key={row.id}>
              <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-base">{row.title}</CardTitle>
                <div className="flex items-center gap-3">
                  <Badge variant="outline">{row.lang.toUpperCase()}</Badge>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={row.active}
                      onCheckedChange={(v) => update(row, { active: v })}
                    />
                    <span className="text-xs text-muted-foreground">
                      {row.active ? "aktiv" : "inaktiv"}
                    </span>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(row.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  value={row.keywords.join(", ")}
                  onChange={(e) =>
                    setRows((rs) =>
                      rs.map((r) =>
                        r.id === row.id
                          ? { ...r, keywords: e.target.value.split(",").map((k) => k.trimStart()) }
                          : r,
                      ),
                    )
                  }
                  placeholder="Stichwörter"
                />
                <Textarea
                  rows={5}
                  value={row.answer}
                  onChange={(e) =>
                    setRows((rs) =>
                      rs.map((r) => (r.id === row.id ? { ...r, answer: e.target.value } : r)),
                    )
                  }
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    update(row, {
                      answer: row.answer,
                      keywords: row.keywords
                        .map((k) => k.trim().toLowerCase())
                        .filter(Boolean),
                    })
                  }
                >
                  <Save className="mr-2 h-4 w-4" /> Änderungen speichern
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminMikrobiAnswers;
